import React, { useCallback, useRef } from 'react';
import { AlertTriangle, Droplet, Flame, HelpCircle, Lock, Trash2, Wifi, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency, formatNumber, formatUnitPrice } from '../utils/utilityServicesGrid.utils';

/**
 * Colores de carátula de medidor, los mismos que ya usa la vista clásica
 * para cada tipo de servicio. Son la única fuente de color "de dominio":
 * el resto de la cuadrícula vive con los tokens del tema.
 */
const SERVICE_STYLES = {
  'Agua': { Icon: Droplet, color: '#3b82f6' },
  'Agua caliente': { Icon: Droplet, color: '#f34d24' },
  'Gas': { Icon: Flame, color: '#d97706' },
  'Electricidad': { Icon: Zap, color: '#eab308' },
  'Internet': { Icon: Wifi, color: '#8b5cf6' },
};

const getServiceStyle = (tipo) =>
  SERVICE_STYLES[tipo] || { Icon: HelpCircle, color: 'hsl(var(--muted-foreground))' };

export const cellKey = (serviceId, dayKey) => `${serviceId}|${dayKey}`;

/** Rayado de la planilla: dos pesos, estructural y interno. */
const RULE = 'border-border/50';
const RULE_STRONG = 'border-border';

/**
 * Planilla mensual de lecturas de medidores: una fila por día del mes y
 * una columna por servicio.
 *
 * La celda no es una caja de formulario sino un renglón de bitácora: el
 * input es transparente y sólo se resalta al pasar el cursor o al tomar
 * el foco, de modo que la cuadrícula se lea como una hoja de lecturas y
 * no como 150 campos apilados. Debajo de cada lectura va el consumo y el
 * costo derivados de la lectura inmediatamente anterior de la cadena.
 *
 * El componente es presentacional: la página contenedora administra los
 * valores, la cadena de cálculo, los errores y los permisos.
 */
const MonthlyServicesGrid = ({
  monthDays = [],
  services = [],
  cells = {},
  baselines = {},
  totals = {},
  canEditExisting = true,
  isSaving = false,
  onCellChange,
  onBaselineChange,
  onDeleteRecord,
}) => {
  const inputRefs = useRef({});

  const registerInput = useCallback((key) => (element) => {
    if (element) inputRefs.current[key] = element;
    else delete inputRefs.current[key];
  }, []);

  /** Enter y flechas verticales recorren la misma columna, día por día. */
  const handleKeyDown = useCallback((event, serviceId, dayIndex) => {
    const offset =
      event.key === 'ArrowDown' || event.key === 'Enter' ? 1
        : event.key === 'ArrowUp' ? -1
          : 0;

    if (offset === 0) return;

    const target = monthDays[dayIndex + offset];
    if (!target) return;

    event.preventDefault();
    const next = inputRefs.current[cellKey(serviceId, target.key)];
    if (next && !next.disabled) {
      next.focus();
      next.select();
    }
  }, [monthDays]);

  if (services.length === 0) {
    return (
      <div className={cn('rounded-md border border-dashed p-12 text-center', RULE_STRONG)}>
        <p className="text-sm text-muted-foreground">
          Ningún servicio visible. Activa al menos uno para registrar lecturas.
        </p>
      </div>
    );
  }

  const columnWidth = services.length <= 3 ? 'min-w-[176px]' : 'min-w-[148px]';

  return (
    <div className={cn('overflow-auto max-h-[68vh] rounded-md border', RULE_STRONG)}>
      <table className="w-full border-separate border-spacing-0 text-sm">
        {/* ---------- CABECERA: UN MEDIDOR POR COLUMNA ---------- */}
        <thead>
          <tr>
            <th
              scope="col"
              className={cn(
                'sticky left-0 top-0 z-30 w-[104px] min-w-[104px] border-b border-r bg-muted px-3 py-2 text-left',
                RULE_STRONG
              )}
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Día
              </span>
            </th>
            {services.map((service) => {
              const { Icon, color } = getServiceStyle(service.tipo);
              return (
                <th
                  key={service._id}
                  scope="col"
                  className={cn(
                    'sticky top-0 z-20 border-b border-r bg-muted px-3 py-2 text-left align-top',
                    RULE_STRONG,
                    columnWidth
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0" color={color} />
                    <span className="truncate font-semibold leading-tight" title={service.nombre}>
                      {service.nombre}
                    </span>
                  </div>
                  <div className="mt-1 flex items-baseline gap-1.5 text-[11px] font-normal">
                    <span className="tracking-wide text-muted-foreground">
                      {service.unidad || 's/u'}
                    </span>
                    <span className="tabular-nums text-muted-foreground/60">
                      {formatUnitPrice(service.precio)}
                    </span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {/* ---------- LECTURA BASE: CIERRE DEL MES ANTERIOR ---------- */}
          <tr>
            <th
              scope="row"
              className={cn(
                'sticky left-0 z-10 border-b border-r bg-muted/60 px-3 py-2 text-left align-middle',
                RULE_STRONG
              )}
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Base
              </span>
            </th>
            {services.map((service) => {
              const baseline = baselines[service._id] || {};
              const isLocked = baseline.source === 'record';
              return (
                <td
                  key={service._id}
                  className={cn('border-b border-r bg-muted/30 px-2 py-1.5 align-top', RULE_STRONG)}
                >
                  {isLocked ? (
                    <div className="px-1.5">
                      <div className="text-right font-medium tabular-nums">
                        {formatNumber(baseline.value)}
                      </div>
                      <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-muted-foreground/70">
                        <Lock className="h-2.5 w-2.5" />
                        {baseline.label || 'cierre anterior'}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="number"
                        step="any"
                        inputMode="decimal"
                        value={baseline.value ?? ''}
                        disabled={isSaving}
                        onChange={(event) => onBaselineChange?.(service._id, event.target.value)}
                        placeholder="lectura inicial"
                        aria-label={`Lectura base de ${service.nombre}`}
                        className="h-7 w-full rounded-sm border border-dashed border-amber-500/50 bg-transparent px-1.5 text-right tabular-nums placeholder:text-[11px] placeholder:normal-case placeholder:text-muted-foreground/60 focus:border-solid focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                      <div className="mt-0.5 text-right text-[10px] text-amber-600 dark:text-amber-400">
                        {baseline.label || 'sin registro previo'}
                      </div>
                    </div>
                  )}
                </td>
              );
            })}
          </tr>

          {/* ---------- UN RENGLÓN POR DÍA ---------- */}
          {monthDays.map((dayInfo, dayIndex) => (
            <tr
              key={dayInfo.key}
              className={cn(
                dayInfo.isWeekend && 'bg-muted/30',
                dayInfo.isFuture && 'opacity-45',
                dayInfo.isToday && 'bg-primary/[0.07]'
              )}
            >
              <th
                scope="row"
                className={cn(
                  'sticky left-0 z-10 border-b border-r px-3 py-1 text-left align-middle',
                  RULE,
                  'border-r-border',
                  dayInfo.isToday
                    ? 'bg-primary/[0.12]'
                    : dayInfo.isWeekend
                      ? 'bg-muted/70'
                      : 'bg-card'
                )}
              >
                <div className="flex items-baseline gap-1.5">
                  <span
                    className={cn(
                      'w-5 text-right text-[15px] font-semibold tabular-nums',
                      dayInfo.isToday && 'text-primary'
                    )}
                  >
                    {dayInfo.day}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
                    {dayInfo.weekdayLabel}
                  </span>
                </div>
              </th>

              {services.map((service) => {
                const key = cellKey(service._id, dayInfo.key);
                const cell = cells[key] || {};
                const isExisting = Boolean(cell.record);
                const isLockedByRole = isExisting && !canEditExisting;
                const isDisabled = isSaving || dayInfo.isFuture || isLockedByRole;
                const chain = cell.chain;

                return (
                  <td
                    key={service._id}
                    className={cn(
                      'group/cell relative border-b border-r px-1.5 py-1 align-top',
                      RULE,
                      cell.error && 'bg-destructive/[0.07]',
                      !cell.error && cell.overLimit && 'bg-amber-500/[0.09]'
                    )}
                  >
                    {/* Marca de cambio pendiente: filete en el canto del renglón */}
                    {cell.dirty && !cell.error && (
                      <span
                        aria-hidden
                        className="absolute inset-y-0 left-0 w-[2px] bg-primary"
                      />
                    )}

                    <div className="relative">
                      <input
                        ref={registerInput(key)}
                        type="number"
                        step="any"
                        inputMode="decimal"
                        value={cell.value ?? ''}
                        disabled={isDisabled}
                        onChange={(event) => onCellChange?.(service._id, dayInfo.key, event.target.value)}
                        onKeyDown={(event) => handleKeyDown(event, service._id, dayIndex)}
                        onFocus={(event) => event.target.select()}
                        placeholder={dayInfo.isFuture ? '' : '—'}
                        aria-label={`Lectura de ${service.nombre} el día ${dayInfo.day}`}
                        title={
                          isLockedByRole
                            ? 'Solo un administrador puede modificar un registro ya guardado'
                            : dayInfo.isFuture
                              ? 'Día futuro: aún no hay lectura que registrar'
                              : undefined
                        }
                        className={cn(
                          'h-7 w-full rounded-sm border bg-transparent px-1.5 text-right tabular-nums transition-colors',
                          'placeholder:text-muted-foreground/40',
                          'hover:bg-accent/50 focus:bg-background focus:outline-none focus:ring-1 focus:ring-ring',
                          'disabled:cursor-not-allowed disabled:hover:bg-transparent',
                          cell.error
                            ? 'border-destructive/70'
                            : cell.dirty
                              ? 'border-primary/50 font-medium'
                              : 'border-transparent focus:border-ring',
                          isExisting && !cell.dirty && 'font-medium'
                        )}
                      />

                      {cell.error && (
                        <AlertTriangle
                          aria-hidden
                          className="pointer-events-none absolute left-1 top-1/2 h-3 w-3 -translate-y-1/2 text-destructive"
                        />
                      )}

                      {isLockedByRole && (
                        <Lock
                          aria-hidden
                          className="pointer-events-none absolute left-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-muted-foreground/60"
                        />
                      )}

                      {/* Eliminar el registro guardado de esta celda */}
                      {isExisting && canEditExisting && !cell.dirty && (
                        <button
                          type="button"
                          disabled={isSaving}
                          onClick={() => onDeleteRecord?.(service._id, dayInfo.key)}
                          title="Eliminar este registro"
                          aria-label={`Eliminar la lectura de ${service.nombre} del día ${dayInfo.day}`}
                          className="absolute left-0 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring group-hover/cell:opacity-100"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>

                    {/* La cadena hecha visible: consumo y costo del día */}
                    <div className="mt-px min-h-[13px] pr-1.5 text-right text-[10px] leading-tight">
                      {cell.error ? (
                        <span className="text-destructive">{cell.error}</span>
                      ) : chain ? (
                        <span
                          className={cn(
                            'tabular-nums',
                            cell.overLimit
                              ? 'font-medium text-amber-600 dark:text-amber-400'
                              : 'text-muted-foreground/70'
                          )}
                          title={
                            cell.overLimit
                              ? `Consumo por encima del límite diario configurado (${formatNumber(service.consumoLimiteDia)} ${service.unidad || ''})`
                              : `Contador anterior: ${formatNumber(chain.contadorAnterior)}`
                          }
                        >
                          {cell.overLimit ? '⚠ ' : ''}
                          Δ{formatNumber(chain.consumo)} · {formatCurrency(chain.costo)}
                        </span>
                      ) : null}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>

        {/* ---------- CIERRE DEL MES ---------- */}
        <tfoot>
          <tr>
            <th
              scope="row"
              className={cn(
                'sticky bottom-0 left-0 z-30 border-r border-t bg-muted px-3 py-2 text-left',
                RULE_STRONG
              )}
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Mes
              </span>
            </th>
            {services.map((service) => {
              const total = totals[service._id] || { consumo: 0, costo: 0, dias: 0 };
              return (
                <td
                  key={service._id}
                  className={cn(
                    'sticky bottom-0 z-20 border-r border-t bg-muted px-3 py-2 text-right align-top',
                    RULE_STRONG
                  )}
                >
                  <div className="font-semibold tabular-nums">
                    {formatNumber(total.consumo)}
                    <span className="ml-1 text-[11px] font-normal text-muted-foreground">
                      {service.unidad || ''}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                    {formatCurrency(total.costo)}
                    <span className="text-muted-foreground/60">
                      {' · '}{total.dias}/{monthDays.length} d
                    </span>
                  </div>
                </td>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default MonthlyServicesGrid;
